import { useEffect, useRef, useState } from 'react';

import { cn, humanShorten } from '@app/libs/utils';

import ArrowDownIcon from '../icons/ArrowDownIcon';
import Checkbox from '../ui/Checkbox';

interface RangeSelectProps {
  label?: string;
  children?: React.ReactNode;
  onChange?: (start: number | undefined, end: number | undefined) => void;
  className?: string;
  start: number | undefined;
  end: number | undefined;
  step?: number;
  min: number;
  max: number;
  dropdownClassName?: string;
  triggerClassName?: string;
  unit?: string;
  onRangeChange?: (start: number | undefined, end: number | undefined) => void;
  onOptionChange?: (option: string) => void;
  align?: 'left' | 'right';
}

interface RangeSliderProps {
  min: number;
  max: number;
  start: number | undefined;
  end: number | undefined;
  step?: number;
  onChange: (start: number | undefined, end: number | undefined) => void;
  unit?: string;
}

interface RadioGroupProps {
  options: { value: string; label: string }[];
  selectedValue: string;
  onChange: (value: string) => void;
}

const RangeSlider: React.FC<RangeSliderProps> = ({
  min,
  max,
  start,
  end,
  step = 1,
  onChange,
  unit,
}) => {
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const [tempStart, setTempStart] = useState(start);
  const [tempEnd, setTempEnd] = useState(end);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Sync state khi props thay đổi
  useEffect(() => {
    setTempStart(start);
  }, [start]);

  useEffect(() => {
    setTempEnd(end);
  }, [end]);

  const snapToStep = (value: number) => {
    if (step && step > 0) {
      return Math.round(value / step) * step;
    }
    return value;
  };

  const handleStartChange = (value: number) => {
    if (isNaN(value) || value === 0) {
      setTempStart(0);
      onChange(0, end);
    } else {
      const snappedValue = snapToStep(value);
      const newStart = Math.max(min, Math.min(max, snappedValue));
      setTempStart(newStart);

      // Đảm bảo start <= end
      if (end !== undefined && newStart > end) {
        onChange(end, newStart);
      } else if (end !== undefined && newStart === end) {
        // Nếu start = end, tăng end thêm 1 step
        const newEnd = Math.min(max, end + step);
        onChange(newStart, newEnd);
      } else {
        onChange(newStart, end);
      }
    }
  };

  const handleEndChange = (value: number) => {
    if (isNaN(value) || value === 0) {
      setTempEnd(undefined);
      onChange(start, undefined);
    } else {
      const snappedValue = snapToStep(value);
      const newEnd = Math.max(min, Math.min(max, snappedValue));
      setTempEnd(newEnd);

      // Đảm bảo start <= end
      if (start !== undefined && newEnd < start) {
        onChange(newEnd, start);
      } else if (start !== undefined && newEnd === start) {
        // Nếu end = start, tăng end thêm 1 step
        const finalEnd = Math.min(max, newEnd + step);
        onChange(start, finalEnd);
      } else {
        onChange(start, newEnd);
      }
    }
  };

  const getValueFromPosition = (clientX: number) => {
    if (!sliderRef.current) return min;

    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

    // Nếu kéo ra hết thanh container (100%), trả về undefined
    if (percentage >= 1.0) {
      return undefined;
    }

    // Nếu kéo đến 90% trở lên, trả về max value
    if (percentage >= 0.9) {
      return max;
    }

    // Nếu kéo dưới 10%, trả về 0
    if (percentage < 0.1) {
      return 0;
    }

    // Nếu kéo đúng 10%, trả về min value
    if (percentage >= 0.1 && percentage < 0.15) {
      return min;
    }

    // Tính giá trị theo tỷ lệ từ 15% đến 90%
    const adjustedPercentage = (percentage - 0.15) / 0.75; // Scale từ 0-1
    const rawValue = min + adjustedPercentage * (max - min);

    // Snap với step
    const snappedValue = snapToStep(rawValue);
    return Math.max(min, Math.min(max, snappedValue));
  };

  const handleMouseDown = (type: 'start' | 'end') => (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(type);

    const value = getValueFromPosition(e.clientX);
    if (type === 'start') {
      setTempStart(value);
    } else {
      setTempEnd(value);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const value = getValueFromPosition(e.clientX);
    if (isDragging === 'start') {
      setTempStart(value);
    } else {
      setTempEnd(value);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      // Đảm bảo thứ tự đúng: start <= end
      let finalStart = tempStart;
      let finalEnd = tempEnd;

      if (finalStart !== undefined && finalEnd !== undefined && finalStart > finalEnd) {
        // Hoán đổi nếu start > end
        [finalStart, finalEnd] = [finalEnd, finalStart];
      } else if (finalStart !== undefined && finalEnd !== undefined && finalStart === finalEnd) {
        // Nếu start = end, tăng end thêm 1 step
        finalEnd = Math.min(max, finalEnd + step);
      }

      onChange(finalStart, finalEnd);
      setIsDragging(null);
    }
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(null);

    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercentage = clickX / rect.width;

    // Tính giá trị từ vị trí click
    const clickValue = getValueFromPosition(e.clientX);

    if (clickValue === undefined) return;

    // Xác định vị trí click so với start và end
    const startPos = getPosition(displayStart);
    const endPos = getPosition(displayEnd);
    const clickPos = getPosition(clickValue);

    // Nếu click trước start
    if (clickPos < startPos) {
      onChange(clickValue, displayEnd);
    }
    // Nếu click sau end
    else if (clickPos > endPos) {
      onChange(displayStart, clickValue);
    }
    // Nếu click giữa start và end
    else {
      // Xem gần start hay end hơn
      const distanceToStart = Math.abs(clickPos - startPos);
      const distanceToEnd = Math.abs(clickPos - endPos);

      if (distanceToStart <= distanceToEnd) {
        // Gần start hơn, di chuyển start
        onChange(clickValue, displayEnd);
      } else {
        // Gần end hơn, di chuyển end
        onChange(displayStart, clickValue);
      }
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, tempStart, tempEnd]);

  const displayStart = isDragging === 'start' ? tempStart : start;
  const displayEnd = isDragging === 'end' ? tempEnd : end;

  const getPosition = (value: number | undefined) => {
    if (value === undefined) return 100;
    if (value === max) return 90;
    if (value === 0) return 5; // Dưới 10% nhưng hiển thị ở 5%
    if (value === min) return 10;
    // Tính position từ 15% đến 90%
    const adjustedPercentage = (value - min) / (max - min);
    return 15 + adjustedPercentage * 75;
  };

  const getWidth = (start: number | undefined, end: number | undefined) => {
    if (start === undefined || end === undefined) return 0;
    if (start === max && end === max) return 0;
    if (start === 0 && end === 0) return 0;
    if (start === 0) return getPosition(end) - getPosition(start);
    if (end === 0) return 0;
    if (start === max) return getPosition(end) - getPosition(start);
    if (end === max) return getPosition(end) - getPosition(start);
    return getPosition(end) - getPosition(start);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-start py-2">
        <span className="text-xs font-semibold text-gray-500 w-1/2">
          Từ:{' '}
          <span className="text-blue-700 text-sm">
            {displayStart === undefined
              ? 'Không giới hạn'
              : displayStart < 1000
                ? displayStart
                : humanShorten(displayStart)}
            {displayStart === undefined ? '' : unit}
          </span>
        </span>
        <span className="text-xs font-semibold text-gray-500 w-1/2">
          Đến:{' '}
          <span className="text-blue-700 text-sm">
            {displayEnd === undefined
              ? 'Không giới hạn'
              : displayEnd < 1000
                ? displayEnd
                : humanShorten(displayEnd)}
            {displayEnd === undefined ? '' : unit}
          </span>
        </span>
      </div>

      <div className="flex items-center gap-11">
        <div className="flex-1 flex items-center gap-2">
          <input
            type="number"
            value={start}
            onChange={e => handleStartChange(Number(e.target.value))}
            min={min}
            max={max}
            step={step}
            className="w-20 px-2 py-2.5 text-sm font-semibold text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 grow transition-all duration-200 ease-in-out"
          />
          <ArrowDownIcon className="w-4 h-4 text-gray-400 rotate-270" />
          <input
            type="number"
            value={end === undefined ? '' : end}
            placeholder="&infin;"
            onChange={e => handleEndChange(Number(e.target.value))}
            min={min}
            max={max}
            step={step}
            className="w-20 px-2 py-2.5 text-sm font-semibold text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 grow transition-all duration-200 ease-in-out"
          />
        </div>
      </div>

      <div className="relative">
        <div
          ref={sliderRef}
          className="h-0.5 bg-gray-200 rounded-full relative cursor-pointer"
          onClick={handleContainerClick}
        >
          {/* Vạch min */}
          <div
            className="absolute top-1/2 w-1 h-3 bg-gray-400 transform -translate-y-1/2"
            style={{ left: '10%' }}
          />

          {/* Vạch max */}
          <div
            className="absolute top-1/2 w-1 h-3 bg-gray-400 transform -translate-y-1/2"
            style={{ left: '90%' }}
          />

          {/* Thanh active */}
          <div
            className="absolute top-1/2 h-0.5 bg-blue-500 transform -translate-y-1/2 transition-all duration-200 ease-in-out"
            style={{
              left: `${getPosition(displayStart)}%`,
              width: `${getWidth(displayStart, displayEnd)}%`,
            }}
          />

          {/* Start handle */}
          <div
            className="absolute top-1/2 w-4 h-4 bg-blue-500 rounded-full transform -translate-y-1/2 cursor-pointer hover:scale-110 hover:shadow-lg transition-all duration-200 ease-in-out"
            style={{ left: `calc(${getPosition(displayStart)}% - 8px)` }}
            onMouseDown={handleMouseDown('start')}
          />

          {/* End handle */}
          <div
            className="absolute top-1/2 w-4 h-4 bg-blue-500 rounded-full transform -translate-y-1/2 cursor-pointer hover:scale-110 hover:shadow-lg transition-all duration-200 ease-in-out"
            style={{ left: `calc(${getPosition(displayEnd)}% - 8px)` }}
            onMouseDown={handleMouseDown('end')}
          />
        </div>
      </div>
    </div>
  );
};

const RadioGroup: React.FC<RadioGroupProps> = ({ options, selectedValue, onChange }) => {
  return (
    <div className="flex flex-col gap-2">
      {options.map(option => (
        <label
          key={option.value}
          className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded transition-colors"
        >
          <div className="relative">
            <Checkbox
              type="radio"
              name="range-option"
              value={option.value}
              checked={selectedValue === option.value}
              onChange={checked => onChange(option.value)}
              label={option.label}
            />
          </div>
        </label>
      ))}
    </div>
  );
};

export const RangeSelect: React.FC<RangeSelectProps> = ({
  label,
  onChange,
  className,
  start,
  end,
  children,
  dropdownClassName = '',
  triggerClassName,
  unit,
  min,
  max,
  step,
  onRangeChange,
  onOptionChange,
  align = 'right',
}) => {
  const rangeRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentStart, setCurrentStart] = useState<number | undefined>(start);
  const [currentEnd, setCurrentEnd] = useState<number | undefined>(end);
  const [dropdownPosition, setDropdownPosition] = useState<{ left?: number; right?: number }>({});

  // Sync state khi props thay đổi
  useEffect(() => {
    setCurrentStart(start);
  }, [start]);

  useEffect(() => {
    setCurrentEnd(end);
  }, [end]);

  const handleRangeChange = (newStart: number | undefined, newEnd: number | undefined) => {
    setCurrentStart(newStart);
    setCurrentEnd(newEnd);
    onRangeChange?.(newStart, newEnd);
    onChange?.(newStart, newEnd);
  };

  const handleOptionChange = (option: string) => {
    onOptionChange?.(option);
  };

  const calculateDropdownPosition = () => {
    if (!rangeRef.current) return;

    const rect = rangeRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const dropdownWidth = 256; // w-64 = 256px

    if (align === 'left') {
      setDropdownPosition({ left: 0 });
    } else {
      // Right align - check if dropdown fits on the right
      const spaceOnRight = viewportWidth - rect.right;
      if (spaceOnRight >= dropdownWidth) {
        setDropdownPosition({ left: 0 });
      } else {
        // Align to right edge of trigger
        setDropdownPosition({ right: 0 });
      }
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      calculateDropdownPosition();
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as any;
      if (rangeRef.current && !rangeRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      calculateDropdownPosition();
    };

    // Use capture phase to ensure we catch the event before other handlers
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, align]);

  return (
    <div
      ref={rangeRef}
      className={cn('flex flex-col gap-2 justify-center relative select-none', className)}
    >
      <button
        onClick={handleToggle}
        className={cn(
          'flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2.5 grow justify-between bg-white cursor-pointer hover:shadow-md transition-shadow',
          triggerClassName
        )}
      >
        <span className="text-xs font-normal text-gray-700 min-w-20 text-left">
          {label ?? 'Lựa chọn'}
        </span>
        <ArrowDownIcon
          className={cn('w-3.5 h-3.5 transition-transform text-gray-700', isOpen && 'rotate-180')}
        />
      </button>

      {isOpen && (
        <div
          className={cn(
            'absolute top-full w-full bg-white border border-gray-200 rounded-xl shadow-lg z-1000 p-4 overflow-y-auto mt-1',
            align === 'left' ? 'left-0' : 'right-0',
            dropdownClassName,
            !dropdownClassName.includes('w-') && 'w-64',
            !dropdownClassName.includes('max-h-') && 'max-h-96',
            isOpen && 'open'
          )}
          style={dropdownPosition}
        >
          <div className="flex flex-col gap-2">
            <RangeSlider
              min={min}
              max={max}
              start={currentStart}
              end={currentEnd}
              step={step}
              onChange={handleRangeChange}
              unit={unit}
            />
            {children}
          </div>
        </div>
      )}
    </div>
  );
};
